import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CommentsService } from '../../service/comments.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'src/environments/environment';
import { SettingsService } from '../../service/settings.service';
import { deepCopy } from 'na-lib';

@Component({
    selector: 'app-users-edit-dialog',
    templateUrl: './users-edit-dialog.component.html',
    styleUrls: ['./users-edit-dialog.component.css'],
    standalone: false
})
export class UsersEditDialogComponent {
  editUserForm!: FormGroup;
  user: any;
  oldUser:any
  hostName: any;
  showBetaWidget: boolean = false;
  userGroupsList:any = []

  availableRoles: { value: string, display: string }[] = [
    { value: 'userManager', display: 'User Manager' },
    { value: 'accountManager', display: 'Workspace Manager' },
    { value: 'contentManager', display: 'DPP Manager' },
    { value: 'workTaskManager', display: 'Tracer Manager' },
    { value: 'tracer', display: 'Tracer User' },
    { value: 'tracingSupervisor', display: 'Tracing Supervisor' },
    { value: 'betaWidgets', display: 'Beta Widgets' }
  ];
  rolesPerContext_ContextID :any
  loggedUser = false

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<UsersEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private commentService: CommentsService,
    private translate: TranslateService,
    private settingsService:SettingsService
  ) {
    dialogRef.disableClose = true;
  }
  
  ngOnInit(): void {
    this.oldUser = deepCopy(this.data.userToEdit)
    this.user = deepCopy(this.data.userToEdit)
    if(this.user.id === this.settingsService.uid$)
      this.loggedUser = true

    const userRoles = this.user.roles || [];
    this.userGroupsList = this.data.userGroupsList ?? []
    const rolesPerContext = this.user.rolesPerContext || {}
    this.rolesPerContext_ContextID = deepCopy(rolesPerContext[this.settingsService.contextId$])

    this.hostName = environment.firebase.projectId
    if (this.hostName == 'narravero-eu-dev' || this.hostName == 'narravero-eu-test') {
      this.showBetaWidget = true
    }

    this.editUserForm = this.fb.group({
      roles:[this.user.roles],
      displayName: [this.user.displayName, Validators.required],
      groupsPerContext:[this.user.groupsPerContext ? this.user.groupsPerContext[this.settingsService.contextId$] : []],
      //email: [this.user.email, [Validators.required, Validators.email]],
      email: [{ value: this.user.email, disabled: true }],
      accountManager: [{value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('accountManager') : userRoles.includes('accountManager'), disabled: this.loggedUser}],
      userManager: [{value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('userManager') : userRoles.includes('userManager'), disabled: this.loggedUser}],
      contentManager: [{value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('contentManager') : userRoles.includes('contentManager'), disabled: this.loggedUser}],
      workTaskManager: [ {value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('workTaskManager') : userRoles.includes('workTaskManager'), disabled: this.loggedUser}],
      tracer: [ {value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('tracer') : userRoles.includes('tracer'), disabled: this.loggedUser}],
      tracingSupervisor: [ {value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('tracingSupervisor') : userRoles.includes('tracingSupervisor'), disabled: this.loggedUser}],
      betaWidgets: [ {value: this.rolesPerContext_ContextID ? this.rolesPerContext_ContextID.includes('betaWidgets') : userRoles.includes('betaWidgets'), disabled: this.loggedUser}],
      rolesPerContext:[ rolesPerContext ]
    });

    const otherControls = ['accountManager', 'contentManager', 'workTaskManager', 'tracer', 'tracingSupervisor', 'userManager'];

    this.editUserForm.get('admin')?.valueChanges.subscribe(isAdmin => {
      if (isAdmin) {
        // disable all if admin is on
        otherControls.forEach(control => {
          this.editUserForm.get(control)?.patchValue(false);
          this.editUserForm.get(control)?.disable();
        });
      } else {
        // enable all if admin is off
        otherControls.forEach(control => {
          this.editUserForm.get(control)?.enable();
        });
      }
    });
  
    // on init disable other if admin is on
    if (this.editUserForm.get('admin')?.value) {
      otherControls.forEach(control => {
        this.editUserForm.get(control)?.patchValue(false); 
        this.editUserForm.get(control)?.disable();
      });
    }
  }

  cancelEdit(){
    this.dialogRef.close([false])
  }
  
  saveEdit(): void {
    if (this.editUserForm.valid) {
      const contextId = this.settingsService.contextId$
      const groupsPerContextForm = this.editUserForm.get('groupsPerContext').value
      let groupsPerContext = this.user.groupsPerContext

      if(groupsPerContext && groupsPerContextForm)
        groupsPerContext[contextId] = groupsPerContextForm
      else
        groupsPerContext = { [contextId] : groupsPerContextForm}

      const formValue = this.editUserForm.getRawValue();
      formValue.groupsPerContext = groupsPerContext
  
      const updatedRoles = this.availableRoles
        .filter(role => formValue[role.value])
        .map(role => role.value);

  
      if(formValue.groupsPerContext?.length==0)
        delete formValue.groupsPerContext

      // Find custom roles that
      const customRoles = this.rolesPerContext_ContextID 
          ? (this.rolesPerContext_ContextID.filter((role: string) =>  !this.availableRoles.some(availableRole => availableRole.value === role)) ?? [])
          : (this.user?.roles?.filter((role: string) =>  !this.availableRoles.some(availableRole => availableRole.value === role)) ?? []);
      // Combine updated with the custom
      const finalRoles = [...updatedRoles, ...customRoles];

      const rolesPerContext = formValue.rolesPerContext
      rolesPerContext[contextId] = finalRoles

      if(this.loggedUser){
        formValue.roles = this.oldUser.roles
        formValue.rolesPerContext = this.oldUser.rolesPerContext
        this.dialogRef.close([true,formValue ]);
      }else
      this.dialogRef.close([true,{ ...formValue, rolesPerContext }]);
    }
  }

  checkIfAllRolesUnselected(){
    const {accountManager, userManager, betaWidgets, contentManager, tracer, tracingSupervisor, workTaskManager, roles, rolesPerContext} = this.editUserForm.value
    const cid = this.settingsService.contextId$

    if(!accountManager  && !userManager && !betaWidgets && !contentManager && !tracer && !tracingSupervisor && !workTaskManager && ( roles?.length==0 || !(rolesPerContext && rolesPerContext[cid] && rolesPerContext[cid]?.length!=0)))
    return true
    else
    return false
  }

  getRoledisplay(role){
    return this.availableRoles.find(avRole => avRole.value === role)?.display
  }


}
